<script lang="ts" setup>
import { onMounted } from 'vue';

import type { AppSvgIconType } from '../appSvgIcon/AppSvgIcon.model';
import AppSvgIcon from '../appSvgIcon/AppSvgIcon.vue';
import AppButton from '../appButton/AppButton.vue';
import AppSlideUpTransition from '../transitions/appSlideUpTransition/AppSlideUpTransition.vue';

interface AppToastButton {
  isVisible: boolean;
  label: string;
}

interface AppToastAutoClose {
  isEnabled: boolean;
  duration: number;
}

export interface AppToastProps {
  message: string;
  icon?: AppSvgIconType | undefined;
  button?: AppToastButton | undefined;
  autoClose?: AppToastAutoClose | undefined;
}

const props = defineProps<AppToastProps>();

interface AppToastEmits {
  (eventName: 'close-toast'): void;
  (eventName: 'button-clicked'): void;
}
const emit = defineEmits<AppToastEmits>();

onMounted(() => {
  if (props.autoClose?.isEnabled) {
    setTimeout(() => {
      emit('close-toast');
    }, props.autoClose.duration);
  }
});
</script>

<template>
  <AppSlideUpTransition>
    <div class="app-toast">
      <div v-if="props.icon" class="app-toast-icon">
        <AppSvgIcon :name="props.icon" />
      </div>
      <div class="app-toast-message">{{ props.message }}</div>
      <div
        v-if="props.button?.isVisible"
        class="app-toast-button"
        @click="emit('button-clicked')"
      >
        <AppButton type="secondary" state="enabled" size="small">
          {{ props.button.label }}
        </AppButton>
      </div>
      <div class="app-toast-close" @click="emit('close-toast')">
        <AppSvgIcon name="close" size="18" />
      </div>
    </div>
  </AppSlideUpTransition>
</template>

<style scoped lang="scss">
@import '../../styles/_media.scss';
@import '../../styles/_fonts.scss';
.app-toast {
  border-radius: var(--border-radius-8);

  background-color: var(--color-neutral-70);
  color: var(--color-white);
  @include font-body-m-light;

  display: flex;
  flex-direction: row;
  gap: var(--spacing-8);
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;

  min-height: calc(var(--spacing-32) + var(--spacing-24));
  padding: var(--spacing-12);
  margin-bottom: var(--spacing-12);
  width: 100%;
  @include for-more-or-equal-than-1200-px {
    width: 500px;
  }

  &:last-of-type {
    margin-bottom: 0;
  }

  @include for-less-or-equal-than-1200-px {
    margin-left: var(--spacing-24);
    margin-right: var(--spacing-24);
    width: calc(100% - var(--spacing-48));
  }

  @include for-less-or-equal-than-599-px {
    margin-left: var(--spacing-16);
    margin-right: var(--spacing-16);
    width: calc(100% - var(--spacing-32));
  }

  .app-toast-message {
    flex-grow: 1;
  }

  .app-toast-close {
    cursor: pointer;
  }
}
</style>
